import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Target, Eye, CheckCircle2 } from 'lucide-react';

const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const differentiators = [
    'Metodología propia de control de obra',
    'Especialización en vivienda multifamiliar',
    'Capacidad en sector institucional',
    'Coordinación de ingenierías superior'
  ];

  return (
    <section 
      id="nosotros" 
      className="py-24 md:py-32 bg-white"
      data-testid="about-section"
      ref={ref}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="mb-16">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-[2px] bg-[#2E75B6]"></div>
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-[#2E75B6]">
                Sobre Nosotros
              </p>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#203864] max-w-2xl">
              Eficacia Profesional en Cada Proyecto
            </h2>
          </motion.div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Left Column - Description */}
            <motion.div variants={itemVariants}>
              <p className="text-lg text-[#7F7F7F] leading-relaxed mb-8">
                Eficapro es una constructora de vanguardia especializada en la materialización de 
                <strong className="text-[#203864]"> infraestructura urbana y residencial de alta densidad</strong>. 
                Nos distinguimos por nuestra capacidad técnica en la ejecución de vivienda multifamiliar 
                y complejos comerciales, institucionales y de servicios.
              </p>
              <p className="text-lg text-[#7F7F7F] leading-relaxed mb-10">
                Nuestro enfoque se centra en la <strong className="text-[#203864]">optimización de procesos constructivos</strong>, 
                garantizando estructuras que cumplen normativas, maximizan rentabilidad y funcionalidad.
              </p>

              {/* Differentiators */}
              <div className="space-y-4">
                {differentiators.map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className="w-8 h-8 bg-[#F8F9FA] flex items-center justify-center flex-shrink-0">
                      <CheckCircle2 size={16} className="text-[#2E75B6]" />
                    </div>
                    <span className="text-[#3B3838] font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right Column - Mission & Vision */}
            <motion.div variants={itemVariants} className="space-y-6">
              {/* Mission */}
              <div className="bg-[#203864] p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-[#2E75B6] flex items-center justify-center">
                    <Target size={24} className="text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Misión</h3>
                </div>
                <p className="text-white/80 leading-relaxed">
                  Transformar el entorno urbano mediante la construcción de espacios multifamiliares 
                  y comerciales de alta calidad, aplicando ingeniería de precisión y una gestión de 
                  recursos eficiente.
                </p>
              </div>

              {/* Vision */}
              <div className="bg-[#F8F9FA] p-8 border-l-4 border-[#2E75B6]">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-white flex items-center justify-center shadow-sm">
                    <Eye size={24} className="text-[#2E75B6]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#203864]">Visión</h3>
                </div>
                <p className="text-[#7F7F7F] leading-relaxed">
                  Posicionarnos para <strong className="text-[#203864]">2030 como la constructora líder 
                  en proyectos de gran escala en México</strong>, siendo referentes en innovación 
                  tecnológica y excelencia en ejecución.
                </p>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
